<template>
    <section :class="[classes.resultBlock.block, classes.resultBlock.blockTypePrefix + block.type]">
        <!-- always present, visually hidden for a single block: the h3 teaser titles need an h2 above them -->
        <h2 :class="[classes.resultBlock.title, { [classes.resultBlock.titleHidden]: !showTitle }]">{{ title }}</h2>
        <div v-if="!block.success" :class="classes.resultBlock.error">{{ translate('agent-search.block.error') }}</div>
        <ol v-else-if="block.items.length" :class="classes.resultBlock.list">
            <li v-for="(item, index) in block.items" :key="item.id" :class="classes.resultBlock.item" :lang="itemLanguage(item) ?? undefined">
                <component
                    :is="teaserFor(item.teaser)"
                    :item="item"
                    :query="query"
                    :position="index + 1"
                    @select="emit('select', { item, position: index + 1 })"
                />
            </li>
        </ol>
        <component
            :is="componentFor('ResultPagination', components)"
            v-if="block.success && (block.pageCount ?? 0) > 1"
            :page="block.page ?? 1"
            :page-count="block.pageCount"
            :query="query"
            :current-search="currentSearch"
            :total="block.total"
            :exact="exactTotal"
            @change="emit('page', $event)"
        />
    </section>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue'
import { itemLanguage } from '../core/lang'
import type { Block, Item } from '../core/types'
import { blockTitle } from '../core/texts'
import { useFallbackLang, useTranslate } from './composables/useTranslate'
import { useClasses } from './composables/useClasses'
import { resolveTeaser, type TeaserMap } from './teasers/registry'
import { componentFor, type ComponentMap } from './registry'

const props = defineProps<{
    block: Block
    query: string
    showTitle: boolean
    currentSearch: string
    teasers?: TeaserMap
    /** Components replacing built-in ones for this instance, before the global registry. */
    components?: ComponentMap
    /** The totals are exact (last page seen); otherwise Google's estimate. */
    exactTotal?: boolean
}>()

const emit = defineEmits<{
    select: [payload: { item: Item; position: number }]
    page: [page: number]
}>()

const translate = useTranslate()
const classes = useClasses()

const lang = useFallbackLang()
const title = computed(() => blockTitle(props.block.type, lang.value))

function teaserFor(key: string): Component {
    return resolveTeaser(key, props.teasers)
}
</script>
